import {Component} from 'preact';
import {LOGIN_URL_WITH_REDIRECT} from '@util/URLUtils';
import LineItemEditable from './Editable';
import LineItemNonEditable from './NonEditable';
import invoke from 'lodash.invoke';
import pathOr from 'ramda/src/pathOr';
import shallowCompare from 'preact-shallow-compare';
import {getLineItemPrice} from '@modules/cart/Cart';
import {toCurrency} from '@util/NumberUtil';

const MAX_VALUE = 99;

export class LineItem extends Component {
    static Editable = LineItemEditable;
    static NonEditable = LineItemNonEditable;

    constructor ( props, context ) {
        super( props );

        const {quantity, product} = props;
        const IS_LOGGED = pathOr( false, ['user', 'isLogged'], context );

        this.state = {
            sku: product.sku,
            quantity: quantity,
            maxQuantity: product.maxQuantity,
            maxQuantityExceeded: this.hasExceededMaxQuantity( quantity, product.maxQuantity ),
            isLogged: IS_LOGGED
        };
    }

    shouldComponentUpdate ( nextProps, nextState ) {
        return shallowCompare( this, nextProps, nextState );
    }

    hasExceededMaxQuantity ( quantity, maxQuantity ) {
        return (maxQuantity != null) && quantity >= maxQuantity;
    }

    componentWillReceiveProps ( {quantity, product} ) {
        const {maxQuantity} = product;

        this.setState( {
            quantity,
            maxQuantity,
            maxQuantityExceeded: this.hasExceededMaxQuantity( quantity, maxQuantity )
        } );
    }

    getLineData () {
        const {sku, quantity} = this.state;
        const {product} = this.props;

        return {quantity, sku, product};
    };

    onInputKeyPress = ( e ) => {
        const data = this.getLineData();

        if ( e.which === 13 ) {
            e.target.blur();
        }

        invoke( this.props, 'onLineInputKeyPress', data, e );
    };

    onInputBlur = e => {
        const data = this.getLineData();
        invoke( this.props, 'onLineInputBlur', data, e );
    };

    onInputChange = e => {
        const {maxQuantity} = this.state;
        const value = parseInt( e.target.value, 10 ) || 0;
        const quantity = maxQuantity != null ? Math.min( value, maxQuantity ) : Math.min( value, MAX_VALUE );

        this.setState( {
            quantity,
            maxQuantityExceeded: this.hasExceededMaxQuantity( quantity, maxQuantity )
        } );

        const data = {
            ...this.getLineData(),
            quantity
        };

        console.log( {data} );
        invoke( this.props, 'onLineInputChange', data, e );
    };

    onDeleteItem = e => {
        const data = this.getLineData();
        invoke( this.props, 'onLineItemDelete', data, e );
    };

    render ( props, state, context ) {
        const {id, product, linePrice} = props;
        const displayPrice = getLineItemPrice( state.quantity, linePrice, product.quantityBreaks );
        const priceRestricted = linePrice === -1;

        const sendProps = {
            id, product, displayPrice, priceRestricted,
            ...state,
            onInputKeyPress: this.onInputKeyPress,
            onInputBlur: this.onInputBlur,
            onInputChange: this.onInputChange,
            onDeleteItem: this.onDeleteItem
        };

        return (
            <div data-testid="cart-line-item">
                {props.children( sendProps )}
            </div>
        );
    }
}

export default LineItem;

/**
 * * **************************************************
 *
 * LineItem Layout Components
 * - Layout helpers for building line item components
 *
 * **************************************************
 */
function formatPrice ( price ) {
    if ( price === 0 ) {
        return 'Free';
    }

    return `$${toCurrency( parseFloat( price, 10 ) )} ea.`;
}

export const Wrapper = ( {id, children} ) =>
    <li id={`lineItem_${id}`}
        className="select-none relative float-none px-4 py-2 border-b border-solid border-grey-light">
        {children}
    </li>;

export const Figure = ( {children} ) =>
    <div className="float-left p-2 mr-1 border border-solid border-grey-lighter bg-white w-16">
        <div className="img-ratio-4x3">
            {children}
        </div>
    </div>;

export const Body = ( {children} ) =>
    <div className="w-auto p-1 overflow-hidden">
        {children}
    </div>;

export const Sku = ( {sku} ) =>
    <div className="font-normal" data-sx-product-sku>
        Item# {sku.replace( /\-$/, '' )}
    </div>;

export const PriceDisplay = ( {price, isLogged, priceRestricted} ) => {
    if ( priceRestricted ) {
        return !isLogged
            ?
            <a href={LOGIN_URL_WITH_REDIRECT} className="text-sm font-normal px-2 inline-block">
                Sign in for price
            </a>
            :
            <span className="text-sm inline-block ml-1 txt-c1">Price unavailable</span>
    }

    return (
        <span className="x-before font-bold inline-block ml-1 txt-c1" data-sx-product-price>
            {formatPrice( price )}
        </span>
    );
};

export const PriceLine = ( {children} ) =>
    <span className="absolute pin-b mb-2">
        {children}
    </span>;

export const QtyField = ( {children} ) =>
    <div className="pin-l inline-block relative">{children}</div>;

export const TitleLink = ( {children, href = '/'} ) =>
    <a href={href}>
        <p data-sx-product-name
           className="text-base font-bold mb-1 block ellipsis">{children}</p>
    </a>;

export const Footer = ( {children} ) =>
    <div className="clear pt-4 w-full">{children}</div>;
